import { InputErrorMessage } from 'components/enrollForm/InputErrorMessage'
import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import {
    Col,
    Container,
    Row,
    Search,
    Spinner,
    TargetList,
    TimedAlert,
    TopBar,
    PopupModal,
    UpdateModal
} from '../../components'
import {
    capitalizeWord,
    clientQuickSearchValidationRules,
    constants,
    getConnectUser,
    getStorage,
    permissionFlags
} from 'helpers'
import { findPermission } from 'helpers/findPermission'
import {
    useBindDispatch,
    useOnChange,
    useValidation,
    useServiceWorkerUpdate,
    useAnalytics
} from 'hooks'
import './home.scss'
import { searchIcon } from 'assets'
import { colors } from 'styles/colors'
import { VillageEnrollmentQr } from 'pages/village/components/VillageEnrollmentQr'

export const Home = () => {
    const { veVillages, zoneSFID, username, role, country } = getConnectUser()

    const [animate, setAnimate] = useState(false)
    const navigate = useNavigate()
    const [targetScrollPos, setTargetScrollPos] = useState(0)
    const { t } = useTranslation()
    const [topBarTitle, setTopBarTitle] = useState(t('welcome'))
    const [changeIconColorTo, setChangeIconColorTo] = useState(false)
    const nodeRef = useRef(null)
    const inputRef = useRef(null)
    const targetScrollRef = useRef(null)
    const { updateUserInfo } = useAnalytics()

    const {
        navbarHandler,
        villageClientListHandler,
        storeEnrollmentFormHandler,
        storeVillageOrdersHandler,
        transactionsHandler,
        fetchInteractionsHandler,
        clientSearchHandler,
        clearClientSearchHandler,
        veCardInventoryHandler,
        homeHandler
    } = useBindDispatch()

    const userPermissions = getStorage(constants.USER_PERMISSIONS)
    const { SHOW_NAVBAR, ANIMATION_TIMEOUT } = constants

    const {
        userData,
        loading,
        error: homePageError
    } = useSelector((store) => store.home)

    const transactionByVillages = useSelector((store) => store.transactions)
    const clientByVillages = useSelector((store) => store.villageClientList)
    const previousClientByVillages = useSelector(
        (store) => store.villagePreviousClientList
    )
    const { permissionsError, permissions } = useSelector(
        ({ userPermissions }) => userPermissions
    )

    const isTransactionsReady = useMemo(() => {
        return Object.values(transactionByVillages || {}).some(
            (transaction) => !transaction.loading
        )
    }, [transactionByVillages])
    const isClientsReady = useMemo(() => {
        return Object.values(clientByVillages || {}).some(
            (client) => !client.loading
        )
    }, [clientByVillages])
    const isPreviousClientsReady = useMemo(() => {
        return Object.values(previousClientByVillages || {}).some(
            (client) => !client.loading
        )
    }, [previousClientByVillages])
    const { updateAvailable, handleUpdate } = useServiceWorkerUpdate()
    const [showUpdateModal, setShowUpdateModal] = useState(updateAvailable)

    const { inputText, onChangeHandler } = useOnChange({
        query: ''
    })

    const queryValidationRules = clientQuickSearchValidationRules()

    const { errors, validateForm } = useValidation(
        inputText,
        queryValidationRules
    )

    const canViewTargetList = findPermission(
        permissions,
        constants.CAN_VIEW_TARGET_LIST
    )

    const canUseFarmerId = findPermission(
        userPermissions,
        permissionFlags.canUseFarmerId
    )

    const TOP_BAR_HEIGHT = 47

    const dataFetchedRef = useRef(false)

    // fetch ve data
    useEffect(() => {
        setAnimate(true)
        // show menu bar
        navbarHandler(SHOW_NAVBAR)

        clearClientSearchHandler()
        localStorage.removeItem(constants.SEARCH_RESULT)

        if (!userData.name) {
            homeHandler({
                veCode: username,
                country
            })
        }

        // set page title
        document.title = t('home.pageTitle')
    }, [])

    useEffect(() => {
        if (!userData.name || dataFetchedRef.current) return

        dataFetchedRef.current = true

        if (veVillages?.length > 0) {
            storeVillageOrdersHandler(veVillages)
            fetchInteractionsHandler({
                villageIds: veVillages?.map((village) => village.sf_id) || [],
                surveyNames: [
                    constants.FARMER_AWARENESS_SURVEY_NAME,
                    constants.TARGET_LIST_SURVEY_NAME
                ]
            })
        }

        veVillages?.forEach((village) => {
            villageClientListHandler({ villageId: village.sf_id })
            transactionsHandler({ villageId: village.sf_id })
        })

        const isEnrollmentFormCached = JSON.parse(
            localStorage.getItem(constants.ENROLLMENT_FORM_CACHED_NAME)
        )
        if (!isEnrollmentFormCached && zoneSFID) {
            storeEnrollmentFormHandler({ zoneSFID })
        }
    }, [userData])

    useEffect(() => {
        if (targetScrollPos < -TOP_BAR_HEIGHT) {
            setTopBarTitle(t('targetList.targetListHeaderTitle'))
        } else {
            setTopBarTitle(t('welcome'))
        }
    }, [targetScrollPos])

    useEffect(() => {
        function getScrollPos() {
            const pos = targetScrollRef.current?.getBoundingClientRect().top
            setTargetScrollPos(pos)
        }

        window.addEventListener('scroll', getScrollPos)
        getScrollPos()
        updateUserInfo(username, role)
        return () => window.removeEventListener('scroll', getScrollPos)
    }, [])

    useEffect(() => {
        validateForm()
        if (inputRef.current) {
            inputRef.current.style.border = 'none'
            setChangeIconColorTo('#16A34A')
        }
    }, [inputText.query, inputRef.current])

    useEffect(() => {
        veCardInventoryHandler(username)
    }, [username])

    const { name } = userData || { name: '' }
    const villageIds = veVillages?.map((village) => village.sf_id) || []

    const handleClientSearch = () => {
        if (!inputText.query || errors?.query) {
            inputRef.current.focus()
            inputRef.current.style.border = '1px solid red'
            return setChangeIconColorTo('red')
        }
        clientSearchHandler({
            searchValue: {
                primaryPhone: inputText.query
            },
            villageIds
        })
        navigate('/client-search/result')
    }

    const homePageContent = (ve) => {
        if (!ve) {
            return
        }

        return (
            <>
                <div className="sticky-header" id="sticky-header">
                    <Container>
                        <TopBar>{topBarTitle}</TopBar>
                    </Container>
                </div>
                <CSSTransition
                    unmountOnExit
                    timeout={ANIMATION_TIMEOUT}
                    in={animate}
                    classNames="generic"
                    appear
                    nodeRef={nodeRef}
                >
                    <div className="page-wrapper" ref={nodeRef}>
                        <Container aria-label="ve-home" className="home-main">
                            {showUpdateModal && (
                                <PopupModal
                                    closeModal={() => setShowUpdateModal(false)}
                                >
                                    <UpdateModal onUpdate={handleUpdate} />
                                </PopupModal>
                            )}
                            <Row className="align-items-center">
                                <Col md={6}>
                                    <h2
                                        className={`home-welcome--text${
                                            canUseFarmerId ? '-large' : ''
                                        } header-welcome`}
                                    >
                                        {t('home.greetings')}{' '}
                                        {capitalizeWord(name?.split(' ')?.[0])}
                                    </h2>
                                </Col>
                            </Row>
                            {canUseFarmerId && (
                                <Row className="justify-content-center">
                                    <Col
                                        md={12}
                                        className="search-input-container"
                                    >
                                        <Search
                                            inputRef={inputRef}
                                            iconSize={30}
                                            iconColor={changeIconColorTo}
                                            iconPos="left"
                                            style="rounded"
                                            name="query"
                                            onChange={onChangeHandler}
                                            onSearchHandler={handleClientSearch}
                                            placeholder={t(
                                                'searchInputPlaceholder'
                                            )}
                                            data-testid="quick-search-input"
                                        />
                                        {errors?.query && (
                                            <InputErrorMessage
                                                errorMessage={errors?.query}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            )}
                            <VillageEnrollmentQr villages={veVillages} />
                            {canViewTargetList && (
                                <TargetList
                                    ve={{
                                        code: username,
                                        ve_villages: veVillages
                                    }}
                                    targetScrollRef={targetScrollRef}
                                    searchIcon={searchIcon({
                                        size: '24',
                                        color: colors.lightGrey
                                    })}
                                    searchIconPos="left"
                                    searchPlaceholder={t(
                                        'targetList.searchPlaceholder'
                                    )}
                                />
                            )}
                        </Container>
                    </div>
                </CSSTransition>
            </>
        )
    }

    const homePage =
        loading ||
        !isTransactionsReady ||
        !isClientsReady ||
        !isPreviousClientsReady ? (
            <Spinner size={'90'} pageSpinner={true} fullscreen={true} />
        ) : homePageError || permissionsError ? (
            <TimedAlert type="floating" status="error">
                {homePageError || permissionsError}
            </TimedAlert>
        ) : (
            homePageContent(userData)
        )

    return homePage
}
